import { usePermissions } from '@core/auth/usePermissions';
import { PermissionGate } from '@shared/components/ui/PermissionGate';
import { Check } from 'lucide-react';

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="ggj-param-group overflow-hidden">
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body">{children}</div>
    </div>
  );
}

const BRAND = 'var(--ggj-accent)';

const PLANS = [
  {
    key: 'starter',
    label: 'Starter',
    price: '₹299/mo',
    seats: 1,
    credits: 60,
    color: '#94a3b8',
    features: ['1 seat', '60 AI credits/mo', 'Core ATS', 'Email support'],
  },
  {
    key: 'team',
    label: 'Team',
    price: '₹999/mo',
    seats: 5,
    credits: 300,
    color: '#22c55e',
    features: ['Up to 5 seats', '300 AI credits/mo', 'Integrations', 'Pipeline automation'],
  },
  {
    key: 'agency',
    label: 'Agency',
    price: '₹2,499/mo',
    seats: 15,
    credits: 1000,
    color: BRAND,
    popular: true,
    features: ['Up to 15 seats', '1,000 AI credits/mo', 'Masking templates', 'Priority support'],
  },
  {
    key: 'enterprise',
    label: 'Enterprise',
    price: '₹7,999/mo',
    seats: 999,
    credits: 5000,
    color: '#f59e0b',
    features: ['Unlimited seats', '5,000 AI credits/mo', 'Custom integrations', 'Dedicated support'],
  },
];

export function BillingPage() {
  const perms = usePermissions();

  if (!perms.canViewBilling) {
    return (
      <PermissionGate
        allowed={false}
        label="Billing — Owner only"
        detail="Only the organisation owner can view billing and plan information."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Billing</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Manage your subscription plan and credits.</p>
      </div>

      {/* ── Available Plans ── */}
      <Section title="Available Plans">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PLANS.map((p) => (
            <div key={p.key}
              className="relative rounded-[10px] border p-4 transition-colors hover:border-[color-mix(in_srgb,var(--vscode-primary)_30%,var(--vscode-border-primary))]"
              style={{ borderColor: `${p.color}30` }}>
              {p.popular && (
                <span className="absolute -top-2 left-3 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                  style={{ background: p.color }}>
                  Popular
                </span>
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold vscode-text-primary">{p.label}</p>
                  <p className="ggj-text-meta vscode-text-tertiary mt-0.5">
                    {p.seats === 999 ? 'Unlimited' : p.seats} seat{p.seats !== 1 && p.seats !== 999 ? 's' : ''} ·{' '}
                    {p.credits.toLocaleString()} credits/mo
                  </p>
                </div>
                <span className="ggj-text-body font-bold" style={{ color: p.color }}>{p.price}</span>
              </div>
              <ul className="space-y-1.5 mb-4">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 ggj-text-meta vscode-text-secondary">
                    <Check className="w-3 h-3 shrink-0" style={{ color: p.color }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button className="btn-secondary w-full ggj-text-meta py-1.5" type="button">
                Select Plan
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Contact for upgrades ── */}
      <Section title="Need Help?">
        <p className="ggj-text-body vscode-text-secondary mb-3">
          To upgrade your plan, purchase additional credits, or discuss a custom contract:
        </p>
        <a href="mailto:business@gograbjob.com"
          className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2.5 ggj-text-body font-medium border vscode-border hover:vscode-bg-hover transition-colors"
          style={{ color: BRAND }}>
          business@gograbjob.com
        </a>
      </Section>

    </div>
  );
}
